import { Component, effect, OnInit } from "@angular/core";
import { ProductService } from "../../../../core/services/product.service";
import { catchError, of } from 'rxjs';
import { PortalService } from "../../../../core/services/portal.service";
import { Router } from '@angular/router';
import { PortalContextService } from "../../../../core/services/portal-context.service";
import { SeoService } from "../../../../core/services/seo.service";

@Component({
  selector: 'app-product-detail',
  standalone: false,
  templateUrl: './product-detail.component.html',
  styleUrl: './product-detail.component.scss'
})
export class ProductDetailComponent implements OnInit {
  groups: any[] = [
    {
      id: 'collateral',
      title: 'Collateral',
      items: [],
    },
    {
      id: 'user_guides',
      title: 'User Guides',
      items: [],
    },
    {
      id: 'certifications',
      title: 'Certifications',
      items: [],
    },
    {
      id: 'configuration_guides',
      title: 'Configuration Guides',
      items: [],
    },
    {
      id: 'videos',
      title: 'Videos',
      items: [],
    },
    {
      id: 'graphics_library',
      title: 'Graphics Library',
      items: [],
    },
    {
      id: 'emulator',
      title: 'Emulator',
      items: [],
    },
    {
      id: 'firmware',
      title: 'Firmware',
      items: [],
    },
    {
      id: 'sdk',
      title: 'SDK',
      items: [],
    },
  ];

  leftList = [] as any[]
  rightList = [] as any[]
  productName = ''
  sku = ''
  productThumnail = ''
  productDesc = ''
  readonly productImageFallbackSrc = 'assets/images/fallback-logo.svg';
  showProductNotFound = false;

  constructor(
    private portalService: PortalService,
    private productService: ProductService,
    private router: Router,
    private portalContextService: PortalContextService,
    private seoService: SeoService
  ) {
    effect(() => {
      if (!this.portalService.loaded()) return;
      const portal = this.portalService.portal()!;
      this.fetchProductDetail(portal)
    });
  }

  ngOnInit(): void {
    this.seoService.setPageSeo({
      title: 'Product | Resource Centre',
      description: 'Browse product downloads and resources.',
      canonicalUrl: this.buildCanonicalUrl(this.router.url || '/product')
    });
  }

  fetchProductDetail(portal: any) {
    const sku = this.router.url.split('/').pop() || ''
    this.showProductNotFound = false;

    const portalData = portal
    const portalId = portalData.id as string
    this.productService.getProductDetail({ portalId, sku }).pipe(
      catchError((err: any) => {
        if (err.status) {
          this.showProductNotFound = true;
        }
        return of(null)
      })
    ).subscribe((resProductDetail: any) => {
      const productDetail = resProductDetail
      if (!productDetail) {
        this.showProductNotFound = true;
        return;
      }
      const toArray = (value: any): any[] => Array.isArray(value) ? value : []

      this.productName = productDetail.name
      this.sku = productDetail.sku
      this.productThumnail = productDetail.thumbnail_url
      this.productDesc = productDetail.description

      const titleCore = [this.productName, this.sku ? `(${this.sku})` : '']
        .join(' ')
        .trim();
      const pageTitle = titleCore ? `${titleCore} | Resource Centre` : 'Product | Resource Centre';
      const descriptionFallback = titleCore
        ? `Find product documents, downloads, and resources for ${titleCore}.`
        : 'Find product documents, downloads, and resources.';
      const canonicalSlug = this.sku || sku;
      this.seoService.setPageSeo({
        title: pageTitle,
        ogTitle: pageTitle,
        description: this.toMetaDescription(this.productDesc, descriptionFallback),
        canonicalUrl: this.buildCanonicalUrl(`/product/${encodeURIComponent(canonicalSlug)}`)
      });

      // mapping group
      for (let i = 0; i < this.groups.length; i++) {
        const key = this.groups[i].id
        this.groups[i].items = []
        if (key === 'collateral') {
          const collateral = toArray(productDetail.collateral)
          this.groups[i].items = collateral.map((guide: any) => ({
            title: guide.name,
            href: guide.url,
            description: guide.description,
            thumbnailUrl: guide.thumbnail_url
          }))
        }
        if (key === 'user_guides') {
          const userGuides = toArray(productDetail.user_guides)
          this.groups[i].items = userGuides.map((guide: any) => ({
            title: guide.name,
            href: guide.url,
            description: guide.description,
            thumbnailUrl: guide.thumbnail_url
          }))
        }
        if (key === 'certifications') {
          const certifications = toArray(productDetail.certifications)
          this.groups[i].items = certifications.map((guide: any) => ({
            title: guide.name,
            href: guide.url,
            description: guide.description,
            thumbnailUrl: guide.thumbnail_url
          }))
        }
        if (key === 'configuration_guides') {
          const configurationGuides = toArray(productDetail.configuration_guides)
          this.groups[i].items = configurationGuides.map((guide: any) => ({
            title: guide.name,
            href: guide.url,
            description: guide.description,
            thumbnailUrl: guide.thumbnail_url
          }))
        }
        if (key === 'graphics_library') {
          const graphicsLibrary = toArray(productDetail.graphics_library)
          if (graphicsLibrary.length > 0) {
            const product_images = [] as any[]
            const packaging = [] as any[]
            graphicsLibrary.forEach((graphic: any) => {
              toArray(graphic?.product_images).forEach((product_image: any) => product_images.push(product_image.url))
              toArray(graphic?.packaging).forEach((pack: any) => packaging.push(pack.url))
            })
            if (product_images.length > 0) {
              this.groups[i].items.push({
                href: '',
                title: `${sku}- Product Images`,
                imageList: product_images
              })
            }
            if (packaging.length > 0) {
              this.groups[i].items.push({
                href: '',
                title: `${sku}- Packaging`,
                imageList: packaging
              })
            }
          }
        }
        if (key === 'sdk') {
          const sdk = toArray(productDetail.sdk)
          this.groups[i].items = sdk.map((guide: any) => ({
            title: guide.name || `${sku}-${guide.version}`,
            href: guide.url,
            description: guide.description,
            thumbnailUrl: guide.thumbnail_url
          }))
        }
        if (key === 'videos') {
          const videos = toArray(productDetail.videos)
          this.groups[i].items = videos.map((guide: any) => ({
            title: guide.name,
            href: guide.url,
            description: guide.description,
            thumbnailUrl: guide.thumbnail_url
          }))
        }
        if (key === 'firmware') {
          const firmware = toArray(productDetail.firmware)
          this.groups[i].items = firmware.map((guide: any) => ({
            title: `${sku} Firmware Version ${guide.version}`,
            href: guide.url,
            description: guide.description,
            thumbnailUrl: guide.thumbnail_url
          }))
        }
        if (key === 'emulator') {
          const emulators = toArray(productDetail.emulators)
          if (emulators.length > 0) {
            this.groups[i].items = emulators.map((emulator: any) => ({
              title: emulator.name || `${sku} Emulator`,
              href: emulator.url,
              description: emulator.description,
              thumbnailUrl: emulator.thumbnail_url
            }))
          } else if (productDetail.emulator) {
            // Backward compatible in case API returns a single emulator object.
            this.groups[i].items.push({
              title: productDetail.emulator.name || `${sku} Emulator`,
              href: productDetail.emulator.url,
              description: productDetail.emulator.description,
              thumbnailUrl: productDetail.emulator.thumbnail_url
            })
          }
        }
      }
      const displayList = toArray(this.groups).filter((g: any) => toArray(g?.items).length > 0)
      const mid = Math.floor(displayList.length / 2)
      this.leftList = []
      this.rightList = []
      for (let i = 0; i < displayList.length; i++) {
        const element = displayList[i];
        if (i <= mid) {
          this.leftList.push(element)
        } else {
          this.rightList.push(element)
        }
      }
    })
  }

  onProductImageError(event: Event): void {
    const target = event.target as HTMLImageElement | null;
    if (!target) {
      return;
    }

    const fallbackSrc = this.productImageFallbackSrc;
    const currentSrc = String(target.getAttribute('src') || target.src || '');
    if (currentSrc.includes(fallbackSrc)) {
      return;
    }

    target.src = fallbackSrc;
  }

  trackById = (_: number, g: { id: string }) => g.id;

  private buildCanonicalUrl(path: string): string {
    const baseUrl = this.portalContextService.getApiBaseUrl().replace(/\/+$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return baseUrl ? `${baseUrl}${normalizedPath}` : normalizedPath;
  }

  private toMetaDescription(raw: string, fallback: string): string {
    const normalized = String(raw || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return normalized || fallback;
  }
}
